import { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
  useLazyGetPayoutDelayEmailPreviewQuery,
  useSendPayoutDelayEmailMutation,
} from "../../../redux/features/admin/admin.api";
import { BaseApiErrorResponse, ModalCompProps } from "../../../utils/types";
import { formatDate } from "../../../utils/helpers";
import { Button } from "../../buttons/button";
import { AppLoader } from "../../loaders/loaders";
import ModalComponent from "..";

const shouldRefreshAfterSendError = (status?: number | string) =>
  status === 404 || status === 409;

const getApiErrorStatus = (response: unknown) =>
  (response as { error?: { status?: number | string } })?.error?.status;

const getApiErrorMessage = (response: unknown) =>
  (response as { error?: BaseApiErrorResponse })?.error?.data?.message;

export const PayoutDelayEmailModal = ({
  open,
  actionId,
  handleClose,
  onRefresh,
}: ModalCompProps & {
  actionId: string;
  onRefresh: () => void;
}) => {
  const sendInProgressRef = useRef(false);
  const [
    fetchPreview,
    {
      data: previewResponse,
      isFetching: fetchingPreview,
      isLoading: loadingPreview,
    },
  ] = useLazyGetPayoutDelayEmailPreviewQuery();
  const [sendPayoutDelayEmail, { isLoading: sendingEmail }] =
    useSendPayoutDelayEmailMutation();

  useEffect(() => {
    if (open && actionId) {
      fetchPreview(actionId, false);
    }
  }, [actionId, fetchPreview, open]);

  const preview = previewResponse?.data;
  const previewLoading = loadingPreview || fetchingPreview;
  const sendDisabled =
    !preview ||
    previewLoading ||
    sendingEmail ||
    sendInProgressRef.current ||
    !preview.eligible ||
    preview.alreadySent ||
    preview.emailStatus === "sending" ||
    preview.emailStatus === "sent";

  const handleSend = async () => {
    if (!actionId || sendDisabled || sendInProgressRef.current) return;

    sendInProgressRef.current = true;
    const response = await sendPayoutDelayEmail(actionId);
    sendInProgressRef.current = false;

    if ("data" in response && response.data?.status === "success") {
      toast.success("Payout delay email sent successfully.");
      handleClose();
      onRefresh();
      fetchPreview(actionId, false);
      return;
    }

    const status = getApiErrorStatus(response);
    const message = getApiErrorMessage(response);

    if (status === 502) {
      toast.error("Unable to send payout delay email. Please try again.");
    } else if (message) {
      toast.error(message);
    }

    if (shouldRefreshAfterSendError(status)) {
      onRefresh();
      fetchPreview(actionId, false);
    }
  };

  return (
    <ModalComponent
      open={open}
      handleClose={handleClose}
      className="md:!max-w-[440px]"
      closeOnOverlayClick={!sendingEmail}
      mobileAnchorBottom
      showBackIcon={false}
      title="Send payout delay email"
      subtitle="Confirm before notifying organizers affected by this holiday."
    >
      <div className="flex flex-col gap-4">
        {previewLoading ? (
          <div className="flex min-h-[180px] items-center justify-center">
            <AppLoader />
          </div>
        ) : preview ? (
          <>
            <div className="rounded-2xl border border-grey-200 bg-grey-50 p-4">
              <p className="text-xs font-bold uppercase tracking-[0.08em] text-grey-500">
                Holiday
              </p>
              <p className="mt-1 text-base font-bold text-grey-950">
                {preview.holidayName}
              </p>
              <p className="mt-1 text-sm text-grey-600">
                {formatDate(preview.holidayDate, "MMMM DD, YYYY", true)}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-grey-200 p-4">
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-grey-500">
                  Organizers
                </p>
                <p className="mt-1 text-2xl font-bold text-grey-950">
                  {preview.recipientCount}
                </p>
              </div>
              <div className="rounded-2xl border border-grey-200 p-4">
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-grey-500">
                  Next Working Day
                </p>
                <p className="mt-1 text-base font-bold text-grey-950">
                  {formatDate(preview.nextWorkingDay, "MMM DD, YYYY", true)}
                </p>
              </div>
            </div>

            {(preview.eligibilityReason || preview.alreadySent) && (
              <p className="rounded-2xl bg-grey-50 p-3 text-sm text-grey-600">
                {preview.alreadySent
                  ? `Already sent${preview.sentAt ? ` on ${formatDate(preview.sentAt, "MMM DD, YYYY", true)}` : ""}.`
                  : preview.eligibilityReason}
              </p>
            )}
          </>
        ) : (
          <p className="rounded-2xl bg-grey-50 p-4 text-sm text-grey-600">
            Email preview could not be loaded.
          </p>
        )}

        <div className="grid grid-cols-2 gap-2">
          <Button
            text="Cancel"
            type="button"
            variation="plain-grey"
            disabled={sendingEmail}
            onClick={handleClose}
          />
          <Button
            text="Send Email"
            type="button"
            variation="primary"
            loading={sendingEmail}
            disabled={sendDisabled}
            onClick={handleSend}
          />
        </div>
      </div>
    </ModalComponent>
  );
};
